/* **`/konto`** — the two-column account screen, from `profile-web-mockups-v2/f-zakladki.html`.
 *
 * The mockup is written in literal CSS; every value below is the token that carries it. Where a
 * number is pure layout — a column width, a row's floor — it is a token in `tokens/spacing.css`
 * under `--*-account-*` rather than a literal here, which is the house rule rather than stylelint's
 * (heights and widths are deliberately outside the value allowlist).
 *
 * ── Why the page is NOT `canonScreen`'s measure ──────────────────────────────────────────────
 * `shared/ui/canonScreen.module.css` gives a v2 screen a bounded `<main>` and a ~1600px measure
 * centred inside it. This screen takes the `.page` — the bound is what stops the document scrolling
 * and the chrome leaving — and NOT the measure, because its two columns run to the frame's edges by
 * design: the left sheet is a `--bg-card` surface with a hairline on its right, and the right
 * column stands on the canvas. The measure lives one level in, on the sheet's content, which is
 * what `--gut` computes.
 *
 * ── The right column is ALWAYS present ───────────────────────────────────────────────────────
 * Every tab puts its one supporting object there, so nothing shifts between tabs. Under 1080px it
 * is hidden outright rather than narrowed — a trainer-code card at 240px is a card nobody can read,
 * and the left column is self-sufficient without it. That number stays a literal in the media query
 * below, because a media feature cannot read a custom property; nothing else in this app reads it,
 * so there is no second opinion for it to disagree with.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The two columns. `minmax(0, 1fr)` on the left so the sheet can actually shrink: a grid item's
 * default `min-width: auto` refuses to go below its content, and the form inside is wider than a
 * 1280px frame minus the side column. */
.columns {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) var(--w-account-side);
  min-height: 0;
}

/* The sheet. `--gut` is the mockup's own: 48px of inset, or whatever it takes to centre a 760px
 * measure once the column is wider than that. Stated once here and read by every row below, which
 * is what keeps the header, the tab strip and the content on one vertical line. */
.sheet {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--bg);
  border-right: var(--border-hairline) solid var(--border);

  --gut: max(var(--space-12), calc((100% - var(--w-account-measure)) / 2));
}

/* The header band: the title, and „Wyloguj się" flush right. It is the `screenHeader` CLASSES
 * rather than the `ScreenHeader` component — that component always draws a `<dl>` of statistics and
 * this screen has none, so an empty definition list plus its row gap would be geometry nobody asked
 * for. `/raporty` takes the same decision for the same reason. */
.header {
  flex: none;
  padding: var(--space-30px) var(--gut) var(--space-0);
}

/* ── The screen's button BOX, worn beside a skin ──────────────────────────────────────────────
 * `ghostButton` / `inkButton` own the treatment and state no geometry — a surface that wears one
 * and declares none renders square at weight 400 — so the box is stated once, here, rather than
 * transcribed onto every control that needs it. The mockup's own cut: 34px with a word in it, not
 * the header's 36px square.
 *
 * `background` stays with the surface: a ghost on the canvas is transparent and a ghost on a card
 * is painted, so the fill is a fact about where the control stands. The weight modifier lives in
 * THIS file beside the base rule, so which of the two wins is settled by source order rather than
 * by which chunk the build emits first. */
.accountButton {
  height: var(--h-account-button);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
  border-radius: var(--radius-10-5px);
}

/* A ghost standing on the SHEET, which is the canvas rather than a card.
 *
 * `ghostButton` deliberately leaves `background` to the surface — a ghost on a card is painted and
 * a ghost on the canvas is not — and a `<button>` with no background of its own renders the user
 * agent's grey button face, which in Flame Dark is a light pill that reads as refused. „Wyloguj
 * się" always declared it inline as `.signOut`; the Subskrypcja tab's portal button and both of
 * this screen's retries needed the same one word, so it is stated once here.
 *
 * Caught on the first read-back of the 21 September picker: „Otwórz portal" rendered as a filled
 * grey slab beside a transparent „Wyloguj się" four rows above it. */
.canvasGhost {
  background: transparent;
}

/* The ink primaries, which the mockup writes a step heavier than the ghosts. */
.accountButtonBold {
  font-weight: var(--fw-bold);
}

.signOut {
  gap: var(--space-2);
  background: transparent;
}

.signOutIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
}

/* The strip's BAR — its box, and nothing `TabStrip` itself declares. 54px tall with the tabs
 * sitting on the bottom hairline, which is where the rule opens. */
.tabBar {
  flex: none;
  gap: var(--space-6);
  align-items: flex-end;
  height: var(--h-account-tabbar);
  padding: var(--space-0) var(--gut);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The one box that scrolls on this screen. `scrollbar-gutter: stable` so a tab whose content
 * overflows does not shift the measure sideways against a tab whose content does not. */
.panel {
  flex: 1;
  min-height: 0;
  overflow: hidden auto;
  scrollbar-gutter: stable;
}

/* The panel's own inset, and the measure. The Profil tab wears `.tight` on top of this, which is
 * the mockup's `.inner.tight` — the cut that makes that tab fit a 1280x720 laptop with no scroll. */
.inner {
  padding: var(--space-7) var(--gut);
}

.tight {
  padding-block: var(--space-6);
}

.lede {
  margin-bottom: var(--space-4);
}

.tight .lede {
  margin-bottom: var(--space-3);
}

.h2 {
  font-size: var(--fs-h2);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-display);
  color: var(--text);
}

.ledeText {
  max-width: var(--w-account-lede);
  margin-top: var(--space-1-5px);
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* A block: the mockup's `.blk`, separated by a rule rather than by a card. */
.block {
  padding: var(--space-5) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.tight .block {
  padding: var(--space-4) var(--space-0);
}

.blockTitle {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
  margin-bottom: var(--space-14px);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* A block of ROWS — the Aplikacja tab's three. The mockup's `.blk.rows`, which drops the block's
 * own padding because each row already carries its own rule. */
.rows {
  padding: var(--space-1) var(--space-0);
}

.row {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-account-row);
  padding: var(--space-3) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.row:first-child {
  border-top: 0;
}

.rowText {
  flex: 1;
  min-width: 0;
}

.rowTitle {
  display: block;
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-account-row);
  color: var(--text);
}

.rowNote {
  display: block;
  margin-top: var(--space-1px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.rowValue {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rowIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
  color: var(--text-2);
}

/* ── The right column ────────────────────────────────────────────────────────────────────── */

/* Centred on its own axis, so a short card sits on the screen's middle line rather than at its top
 * — the mockup's arrangement, and what makes the column read as a companion to the sheet rather
 * than as a second column of content. */
.side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-7) var(--space-8);
  overflow: hidden auto;
  background: var(--bg-nav);
}

.sidePanel {
  width: 100%;
  max-width: var(--w-account-side-card);
}

/* A card in that column: the mockup's `.scard`, at its own 22px, and an ORDINARY card — the column
 * under it is `--bg-nav`, the rail's own near-black, so a card standing on it is the plain
 * `--bg-card` this app draws everywhere else rather than a card-in-card.
 *
 * `--space-22px`, not `--space-5-5px`: the `-px` tokens are their own NUMBER, so the scale's 5.5th
 * step is 5.5 pixels. The card was drawing a 5.5px inset, which is the „cramped at the top" the
 * owner reported. */
.card {
  padding: var(--space-22px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-card);
}

/* The mockup's `.scard .row:first-of-type`, transcribed as it is written — the other half of the
 * „cramped at the top". It is true of TWO rows in this card, because the version row is a `div`
 * among links and is therefore the first of its own type; the mockup has the same shape and draws
 * the same two gaps, and the card measures 306px tall because of it. Reproduced rather than
 * corrected: this is the canon, and narrowing it to one row makes the card 8px short of it. */
.card .row:first-of-type {
  margin-top: var(--space-2);
}

.cardTitle {
  margin-bottom: var(--space-1);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.cardBody {
  font-size: var(--fs-label);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* Below this the sheet has the frame to itself. Hidden rather than narrowed — see the header. */
@media (width < 1080px) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }

  .side {
    display: none;
  }

  .sheet {
    border-right: 0;
  }
}
